import React, { useState, useEffect } from 'react';
import { micrositeAPI, projectAPI } from '../../lib/api';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Layout, Palette, Settings, Eye, Save, Send, Plus,
  Image, FileText, Phone, MessageCircle, MapPin, Video, List,
  Star, Building2, IndianRupee, Calendar, Users, Check, X,
  GripVertical, Trash2, ChevronUp, ChevronDown, Sparkles, Smartphone, Monitor
} from 'lucide-react';
import MicrositePreview from '../../components/MicrositePreview';

// Real Estate Landing Page Templates
const TEMPLATES = [
  {
    id: 'luxury',
    name: 'Luxury Living',
    description: 'Premium template for high-end properties',
    thumbnail: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&h=300&fit=crop',
    primaryColor: '#1a1a2e',
    accentColor: '#d4af37',
    sections: ['hero', 'highlights', 'gallery', 'amenities', 'floor_plans', 'pricing', 'location', 'lead_form', 'contact']
  },
  {
    id: 'modern',
    name: 'Modern Minimalist',
    description: 'Clean and contemporary design',
    thumbnail: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400&h=300&fit=crop',
    primaryColor: '#0f172a',
    accentColor: '#3b82f6',
    sections: ['hero', 'about', 'gallery', 'amenities', 'pricing', 'lead_form', 'whatsapp']
  },
  {
    id: 'family',
    name: 'Family Home',
    description: 'Warm and inviting for residential projects',
    thumbnail: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=300&fit=crop',
    primaryColor: '#065f46',
    accentColor: '#f59e0b',
    sections: ['hero', 'highlights', 'gallery', 'amenities', 'nearby', 'testimonials', 'lead_form', 'call_now']
  },
  {
    id: 'urban',
    name: 'Urban Living',
    description: 'Perfect for city apartments and condos',
    thumbnail: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400&h=300&fit=crop',
    primaryColor: '#18181b',
    accentColor: '#ec4899',
    sections: ['hero', 'video', 'highlights', 'gallery', 'amenities', 'pricing', 'lead_form', 'whatsapp']
  },
  {
    id: 'starter',
    name: 'Starter Home',
    description: 'Simple and effective for affordable housing',
    thumbnail: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=400&h=300&fit=crop',
    primaryColor: '#1e3a5f',
    accentColor: '#22c55e',
    sections: ['hero', 'highlights', 'pricing', 'lead_form', 'call_now']
  },
  {
    id: 'blank',
    name: 'Start from Scratch',
    description: 'Build your own custom layout',
    thumbnail: null,
    primaryColor: '#0f172a',
    accentColor: '#b45309',
    sections: ['hero', 'lead_form']
  }
];

// Available Widgets/Sections
const WIDGETS = [
  { id: 'hero', name: 'Hero Banner', icon: Image, description: 'Full-width hero image with title', category: 'essential' },
  { id: 'about', name: 'About Project', icon: FileText, description: 'Project description and details', category: 'content' },
  { id: 'highlights', name: 'Key Highlights', icon: Star, description: 'USPs and key features', category: 'content' },
  { id: 'gallery', name: 'Image Gallery', icon: Image, description: 'Photo gallery with lightbox', category: 'content' },
  { id: 'video', name: 'Video Tour', icon: Video, description: 'Embedded video walkthrough', category: 'content' },
  { id: 'amenities', name: 'Amenities', icon: List, description: 'List of amenities with icons', category: 'content' },
  { id: 'floor_plans', name: 'Floor Plans', icon: Layout, description: 'BHK options with floor plans', category: 'content' },
  { id: 'pricing', name: 'Pricing Table', icon: IndianRupee, description: 'Price list by unit type', category: 'content' },
  { id: 'nearby', name: 'Nearby Places', icon: MapPin, description: 'Schools, hospitals, malls nearby', category: 'content' },
  { id: 'location', name: 'Location Map', icon: MapPin, description: 'Google Maps embed', category: 'content' },
  { id: 'testimonials', name: 'Testimonials', icon: Users, description: 'Customer reviews', category: 'content' },
  { id: 'lead_form', name: 'Lead Capture Form', icon: FileText, description: 'Collect visitor enquiries', category: 'conversion', essential: true },
  { id: 'whatsapp', name: 'WhatsApp Chat', icon: MessageCircle, description: 'Floating WhatsApp button', category: 'conversion' },
  { id: 'call_now', name: 'Click to Call', icon: Phone, description: 'Floating call button', category: 'conversion' },
  { id: 'contact', name: 'Contact Info', icon: Phone, description: 'Address and contact details', category: 'conversion' },
  { id: 'brochure', name: 'Download Brochure', icon: FileText, description: 'PDF download button', category: 'conversion' },
];

const MicrositeBuilderPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  
  const [step, setStep] = useState(isEditing ? 'builder' : 'template');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('sections');
  const [previewMode, setPreviewMode] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop'); // 'desktop' or 'mobile'
  
  const [microsite, setMicrosite] = useState({
    project_id: '',
    title: '',
    slug: '',
    description: '',
    sections: [],
    settings: {
      primaryColor: '#0f172a',
      accentColor: '#b45309',
      whatsappNumber: '',
      phoneNumber: '',
      showPartnerInfo: true,
    }
  });

  // Get selected project details for preview
  const selectedProject = projects.find(p => p.id === microsite.project_id);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const projectsRes = await projectAPI.list();
      setProjects(projectsRes.data);
      
      if (isEditing) {
        const micrositeRes = await micrositeAPI.get(id);
        setMicrosite({
          ...micrositeRes.data,
          settings: micrositeRes.data.settings || {
            primaryColor: '#0f172a',
            accentColor: '#b45309',
            whatsappNumber: '',
            phoneNumber: '',
            showPartnerInfo: true,
          }
        });
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    const initialSections = template.sections.map((sectionId, index) => ({
      id: `${sectionId}-${Date.now()}-${index}`,
      type: sectionId,
      order: index,
      is_visible: true,
      content: getDefaultContent(sectionId)
    }));
    
    setMicrosite(prev => ({
      ...prev,
      sections: initialSections,
      settings: {
        ...prev.settings,
        primaryColor: template.primaryColor,
        accentColor: template.accentColor,
      }
    }));
    setStep('details');
  };

  const getDefaultContent = (sectionType) => {
    const defaults = {
      hero: { title: '', subtitle: '', backgroundImage: '' },
      about: { title: 'About the Project', text: '' },
      highlights: { title: 'Key Highlights', items: ['Premium Location', 'World-class Amenities', 'RERA Approved', 'Ready to Move'] },
      gallery: { title: 'Gallery', images: [] },
      video: { title: 'Virtual Tour', videoUrl: '' },
      amenities: { title: 'Amenities', items: ['Swimming Pool', 'Gymnasium', 'Clubhouse', 'Children Play Area', 'Landscaped Gardens'] },
      floor_plans: { title: 'Floor Plans', plans: [] },
      pricing: { title: 'Pricing', units: [] },
      nearby: { title: 'Nearby Attractions', places: [] },
      location: { title: 'Location', address: '', mapUrl: '' },
      testimonials: { title: 'What Our Customers Say', reviews: [] },
      lead_form: { title: 'Enquire Now', subtitle: 'Get exclusive offers and pricing details', fields: ['name', 'phone', 'email'] },
      whatsapp: { message: 'Hi! I am interested in this property.' },
      call_now: { label: 'Call Now' },
      contact: { title: 'Contact Us', address: '', phone: '', email: '' },
      brochure: { title: 'Download Brochure', buttonText: 'Download Now', fileUrl: '' },
    };
    return defaults[sectionType] || {};
  };

  const handleDetailsSubmit = () => {
    if (!microsite.project_id || !microsite.title || !microsite.slug) {
      toast.error('Please fill in all required fields');
      return;
    }
    setStep('builder');
  };

  const addSection = (widgetId) => {
    const newSection = {
      id: `${widgetId}-${Date.now()}`,
      type: widgetId,
      order: microsite.sections.length,
      is_visible: true,
      content: getDefaultContent(widgetId)
    };
    setMicrosite(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
    toast.success(`Added ${WIDGETS.find(w => w.id === widgetId)?.name}`);
  };

  const removeSection = (sectionId) => {
    setMicrosite(prev => ({
      ...prev,
      sections: prev.sections.filter(s => s.id !== sectionId)
    }));
  };

  const moveSection = (index, direction) => {
    const newSections = [...microsite.sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newSections.length) return;
    
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    newSections.forEach((s, i) => s.order = i);
    setMicrosite(prev => ({ ...prev, sections: newSections }));
  };

  const toggleSectionVisibility = (sectionId) => {
    setMicrosite(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, is_visible: !s.is_visible } : s
      )
    }));
  };

  const updateSectionContent = (sectionId, content) => {
    setMicrosite(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.id === sectionId ? { ...s, content: { ...s.content, ...content } } : s
      )
    }));
  };

  const handleSave = async (submitForApproval = false) => {
    setSaving(true);
    try {
      if (isEditing) {
        await micrositeAPI.update(id, {
          title: microsite.title,
          description: microsite.description,
          sections: microsite.sections,
        });
      } else {
        const response = await micrositeAPI.create({
          project_id: microsite.project_id,
          title: microsite.title,
          slug: microsite.slug,
          description: microsite.description,
        });
        
        // Update with sections
        await micrositeAPI.update(response.data.id, {
          sections: microsite.sections,
        });
        
        if (submitForApproval) {
          await micrositeAPI.submitForApproval(response.data.id);
        }
      }
      
      toast.success(submitForApproval ? 'Submitted for approval!' : 'Microsite saved!');
      navigate(-1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  // Step 1: Template Selection
  if (step === 'template') {
    return (
      <div className="space-y-6" data-testid="template-selection">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Choose a Template</h1>
            <p className="text-muted-foreground">Select a starting point for your microsite</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TEMPLATES.map((template) => (
            <Card 
              key={template.id} 
              className={`cursor-pointer transition-all hover:shadow-lg hover:border-amber-400 ${
                selectedTemplate?.id === template.id ? 'ring-2 ring-amber-500' : ''
              }`}
              onClick={() => handleSelectTemplate(template)}
              data-testid={`template-${template.id}`}
            >
              <div className="aspect-video bg-muted relative overflow-hidden rounded-t-lg">
                {template.thumbnail ? (
                  <img 
                    src={template.thumbnail} 
                    alt={template.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                    <Plus className="h-12 w-12 text-slate-400" />
                  </div>
                )}
                <div 
                  className="absolute bottom-2 right-2 flex gap-1"
                >
                  <div 
                    className="w-6 h-6 rounded-full border-2 border-white shadow"
                    style={{ backgroundColor: template.primaryColor }}
                  />
                  <div 
                    className="w-6 h-6 rounded-full border-2 border-white shadow"
                    style={{ backgroundColor: template.accentColor }}
                  />
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-lg">{template.name}</h3>
                <p className="text-sm text-muted-foreground">{template.description}</p>
                <div className="flex flex-wrap gap-1 mt-3">
                  {template.sections.slice(0, 4).map((s) => (
                    <Badge key={s} variant="secondary" className="text-xs">
                      {WIDGETS.find(w => w.id === s)?.name || s}
                    </Badge>
                  ))}
                  {template.sections.length > 4 && (
                    <Badge variant="secondary" className="text-xs">
                      +{template.sections.length - 4} more
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Step 2: Basic Details
  if (step === 'details') {
    return (
      <div className="max-w-xl mx-auto space-y-6" data-testid="microsite-details">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => setStep('template')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Microsite Details</h1>
            <p className="text-muted-foreground">Set up basic information</p>
          </div>
        </div>

        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label>Select Project *</Label>
              <select
                value={microsite.project_id}
                onChange={(e) => setMicrosite(prev => ({ ...prev, project_id: e.target.value }))}
                className="w-full h-10 px-3 rounded-md border border-input bg-background"
                required
                data-testid="select-project"
              >
                <option value="">Choose a project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} - {p.location}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label>Microsite Title *</Label>
              <Input
                value={microsite.title}
                onChange={(e) => setMicrosite(prev => ({ 
                  ...prev, 
                  title: e.target.value,
                  slug: generateSlug(e.target.value)
                }))}
                placeholder="e.g., Prestige Lakeside - Premium 3BHK Apartments"
                data-testid="input-title"
              />
            </div>

            <div className="space-y-2">
              <Label>URL Slug *</Label>
              <Input
                value={microsite.slug}
                onChange={(e) => setMicrosite(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="prestige-lakeside"
                data-testid="input-slug"
              />
              <p className="text-xs text-muted-foreground">
                Your microsite URL: /m/{microsite.slug || 'your-slug'}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={microsite.description}
                onChange={(e) => setMicrosite(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of your microsite..."
                rows={3}
              />
            </div>

            <Button onClick={handleDetailsSubmit} className="w-full" data-testid="btn-continue">
              Continue to Builder
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 3: Builder
  return (
    <div className="space-y-4" data-testid="microsite-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">{microsite.title || 'Microsite Builder'}</h1>
            <p className="text-sm text-muted-foreground">Customize your landing page</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setPreviewMode(!previewMode)}>
            <Eye className="h-4 w-4 mr-2" />
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            Save Draft
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving} data-testid="btn-submit">
            <Send className="h-4 w-4 mr-2" />
            Submit for Approval
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Panel - Widgets & Settings */}
        <div className="lg:col-span-1 space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="sections">
                <Layout className="h-4 w-4 mr-2" />
                Sections
              </TabsTrigger>
              <TabsTrigger value="settings">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sections" className="mt-4 space-y-4">
              {/* Conversion Widgets */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">CONVERSION WIDGETS</h3>
                <div className="grid grid-cols-2 gap-2">
                  {WIDGETS.filter(w => w.category === 'conversion').map((widget) => {
                    const Icon = widget.icon;
                    const isAdded = microsite.sections.some(s => s.type === widget.id);
                    return (
                      <Button
                        key={widget.id}
                        variant={isAdded ? "secondary" : "outline"}
                        size="sm"
                        className="justify-start h-auto py-2 px-3"
                        onClick={() => addSection(widget.id)}
                        disabled={widget.essential && isAdded}
                        data-testid={`add-${widget.id}`}
                      >
                        <Icon className="h-4 w-4 mr-2 shrink-0" />
                        <span className="truncate text-xs">{widget.name}</span>
                        {isAdded && <Check className="h-3 w-3 ml-auto text-emerald-600" />}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Content Widgets */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">CONTENT SECTIONS</h3>
                <div className="grid grid-cols-2 gap-2">
                  {WIDGETS.filter(w => w.category === 'content').map((widget) => {
                    const Icon = widget.icon;
                    return (
                      <Button
                        key={widget.id}
                        variant="outline"
                        size="sm"
                        className="justify-start h-auto py-2 px-3"
                        onClick={() => addSection(widget.id)}
                        data-testid={`add-${widget.id}`}
                      >
                        <Icon className="h-4 w-4 mr-2 shrink-0" />
                        <span className="truncate text-xs">{widget.name}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Essential */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">ESSENTIAL</h3>
                <div className="grid grid-cols-2 gap-2">
                  {WIDGETS.filter(w => w.category === 'essential').map((widget) => {
                    const Icon = widget.icon;
                    return (
                      <Button
                        key={widget.id}
                        variant="outline"
                        size="sm"
                        className="justify-start h-auto py-2 px-3"
                        onClick={() => addSection(widget.id)}
                        data-testid={`add-${widget.id}`}
                      >
                        <Icon className="h-4 w-4 mr-2 shrink-0" />
                        <span className="truncate text-xs">{widget.name}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="settings" className="mt-4 space-y-4">
              <Card>
                <CardContent className="p-4 space-y-4">
                  <div className="space-y-2">
                    <Label>WhatsApp Number</Label>
                    <Input
                      value={microsite.settings.whatsappNumber}
                      onChange={(e) => setMicrosite(prev => ({
                        ...prev,
                        settings: { ...prev.settings, whatsappNumber: e.target.value }
                      }))}
                      placeholder="+91 9876543210"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Phone Number (Click to Call)</Label>
                    <Input
                      value={microsite.settings.phoneNumber}
                      onChange={(e) => setMicrosite(prev => ({
                        ...prev,
                        settings: { ...prev.settings, phoneNumber: e.target.value }
                      }))}
                      placeholder="+91 9876543210"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={microsite.settings.primaryColor}
                        onChange={(e) => setMicrosite(prev => ({
                          ...prev,
                          settings: { ...prev.settings, primaryColor: e.target.value }
                        }))}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={microsite.settings.primaryColor}
                        onChange={(e) => setMicrosite(prev => ({
                          ...prev,
                          settings: { ...prev.settings, primaryColor: e.target.value }
                        }))}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Accent Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={microsite.settings.accentColor}
                        onChange={(e) => setMicrosite(prev => ({
                          ...prev,
                          settings: { ...prev.settings, accentColor: e.target.value }
                        }))}
                        className="w-12 h-10 p-1"
                      />
                      <Input
                        value={microsite.settings.accentColor}
                        onChange={(e) => setMicrosite(prev => ({
                          ...prev,
                          settings: { ...prev.settings, accentColor: e.target.value }
                        }))}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label>Show Partner Info</Label>
                    <Switch
                      checked={microsite.settings.showPartnerInfo}
                      onCheckedChange={(checked) => setMicrosite(prev => ({
                        ...prev,
                        settings: { ...prev.settings, showPartnerInfo: checked }
                      }))}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right Panel - Section List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Layout className="h-5 w-5" />
                Page Sections ({microsite.sections.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {microsite.sections.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Layout className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No sections added yet</p>
                  <p className="text-sm">Add sections from the left panel</p>
                </div>
              ) : (
                microsite.sections.map((section, index) => {
                  const widget = WIDGETS.find(w => w.id === section.type);
                  const Icon = widget?.icon || Layout;
                  
                  return (
                    <div
                      key={section.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        section.is_visible ? 'bg-white' : 'bg-muted/50 opacity-60'
                      }`}
                    >
                      <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
                      
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                        widget?.category === 'conversion' ? 'bg-emerald-100' : 'bg-blue-100'
                      }`}>
                        <Icon className={`h-5 w-5 ${
                          widget?.category === 'conversion' ? 'text-emerald-600' : 'text-blue-600'
                        }`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{widget?.name || section.type}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {widget?.description}
                        </p>
                      </div>

                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => moveSection(index, 'up')}
                          disabled={index === 0}
                        >
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => moveSection(index, 'down')}
                          disabled={index === microsite.sections.length - 1}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => toggleSectionVisibility(section.id)}
                        >
                          {section.is_visible ? (
                            <Eye className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4 opacity-50" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => removeSection(section.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>

          {/* Quick Tips */}
          <Card className="mt-4 bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <h3 className="font-semibold flex items-center gap-2 text-amber-800">
                <Sparkles className="h-4 w-4" />
                Pro Tips
              </h3>
              <ul className="mt-2 text-sm text-amber-700 space-y-1">
                <li>• Add WhatsApp Chat for instant visitor engagement</li>
                <li>• Lead Form is essential - don't remove it!</li>
                <li>• Use Gallery to showcase project images</li>
                <li>• Add Pricing to filter serious buyers</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog open={previewMode} onOpenChange={setPreviewMode}>
        <DialogContent className="max-w-6xl h-[90vh] p-0 flex flex-col">
          {/* Preview Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50 shrink-0">
            <div className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-slate-600" />
              <span className="font-semibold">Preview: {microsite.title}</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Device Toggle */}
              <div className="flex items-center bg-white rounded-lg border p-1">
                <button
                  onClick={() => setPreviewDevice('desktop')}
                  className={`p-2 rounded ${previewDevice === 'desktop' ? 'bg-slate-100' : ''}`}
                >
                  <Monitor className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setPreviewDevice('mobile')}
                  className={`p-2 rounded ${previewDevice === 'mobile' ? 'bg-slate-100' : ''}`}
                >
                  <Smartphone className="h-4 w-4" />
                </button>
              </div>
              <Button variant="outline" size="sm" onClick={() => setPreviewMode(false)}>
                <X className="h-4 w-4 mr-1" />
                Close
              </Button>
            </div>
          </div>
          
          {/* Preview Content - Scrollable */}
          <div className="flex-1 overflow-y-auto bg-slate-200 p-4">
            <div 
              className={`mx-auto bg-white shadow-2xl transition-all duration-300 ${
                previewDevice === 'mobile' 
                  ? 'w-[375px] rounded-[2rem] border-[8px] border-slate-800' 
                  : 'w-full max-w-5xl rounded-lg'
              }`}
            >
              <div 
                className={`${previewDevice === 'mobile' ? 'rounded-[1.5rem] h-[650px] overflow-y-auto' : 'rounded-lg'}`}
              >
                <MicrositePreview 
                  microsite={microsite} 
                  project={selectedProject}
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MicrositeBuilderPage;
